import { Play, Plus } from "lucide-react";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { InputGroup } from "@/components/ui/input-group";
import { minutesToSeconds, parseDurationInput } from "@/lib/duration";
import { formatDurationSeconds } from "@/lib/format";
import {
    useAddTimerEntryMutation,
    useDeleteTimerEntryMutation,
    usePauseTimerMutation,
    useResumeTimerMutation,
    useStartTimerMutation,
    useUpdateTimerDurationMutation,
} from "@/mutations/timer-mutations";
import { useSessionQuery } from "@/queries/queries";
import { useTimerEntries } from "@/queries/timer-queries";
import { TimerEntryRow } from "./TimerEntryRow";
import { useElapsedSeconds } from "./useElapsedSeconds";

type TimerPanelProps = {
    taskId: number;
    customAttributeId: number;
};

export function TimerPanel({ taskId, customAttributeId }: TimerPanelProps) {
    const field = { taskId, customAttributeId };
    const [draft, setDraft] = useState("");

    const { data, isPending } = useTimerEntries(field);
    const { data: session } = useSessionQuery();
    const currentUserId = session?.user?.id;
    const start = useStartTimerMutation(field);
    const add = useAddTimerEntryMutation(field);
    const resume = useResumeTimerMutation(field);
    const pause = usePauseTimerMutation(field);
    const updateDuration = useUpdateTimerDurationMutation(field);
    const remove = useDeleteTimerEntryMutation(field);

    // The one button does double duty: it starts recording while the input is empty,
    // and becomes "Add" the moment a duration is typed into it.
    const hasDraft = draft.trim() !== "";
    const draftMinutes = hasDraft ? parseDurationInput(draft) : null;
    const isDraftValid = draftMinutes !== null && draftMinutes > 0;
    const isBusy =
        start.isPending ||
        add.isPending ||
        resume.isPending ||
        pause.isPending ||
        updateDuration.isPending ||
        remove.isPending;

    const handlePrimaryAction = () => {
        if (!hasDraft) {
            start.mutate(field);
            return;
        }
        if (!isDraftValid) return;
        add.mutate({ ...field, seconds: minutesToSeconds(draftMinutes) }, { onSuccess: () => setDraft("") });
    };

    const entries = data?.entries ?? [];
    // The server's total is the committed one; add the running segment so this agrees
    // with the field above it and with the recording row below.
    const runningEntry = entries.find((entry) => entry.startedAt !== null);
    const totalSeconds = useElapsedSeconds(data?.totalSeconds ?? 0, runningEntry?.startedAt ?? null);

    return (
        <div className="w-72">
            {/* Same shell as a command palette: a padded input row over a scrollable list. */}
            <div className="flex items-center gap-1 p-1">
                <InputGroup className="h-7! flex-1 rounded-md! border-input/30 bg-input/30 shadow-none!">
                    <input
                        type="text"
                        value={draft}
                        placeholder="1h 30m"
                        aria-label="Duration to add"
                        className="w-full px-2 outline-hidden placeholder:text-orsane-placeholder"
                        onChange={(event) => setDraft(event.target.value)}
                        onKeyDown={(event) => {
                            // The task table binds Space and arrow keys; typing here must not reach it.
                            event.stopPropagation();
                            if (event.key === "Enter") {
                                event.preventDefault();
                                handlePrimaryAction();
                            }
                        }}
                    />
                </InputGroup>
                <Button
                    size="xs"
                    variant="outline"
                    onClick={handlePrimaryAction}
                    disabled={isBusy || (hasDraft && !isDraftValid)}
                    className="h-7"
                >
                    {hasDraft ? <Plus /> : <Play />}
                    {hasDraft ? "Add" : "Timer"}
                </Button>
            </div>

            <div className="mx-0 h-px bg-border" />

            <div className="no-scrollbar max-h-64 overflow-y-auto p-1">
                {isPending && <div className="px-2 py-3 text-center text-muted-foreground">Loading…</div>}
                {!isPending && entries.length === 0 && (
                    <div className="px-2 py-3 text-center text-muted-foreground">No time tracked yet</div>
                )}
                {entries.map((entry) => (
                    <TimerEntryRow
                        key={entry.id}
                        entry={entry}
                        disabled={isBusy}
                        isOwn={entry.userId === currentUserId}
                        onPlay={(entryId) => resume.mutate({ entryId })}
                        onPause={(entryId) => pause.mutate({ entryId })}
                        onDelete={(entryId) => remove.mutate({ entryId })}
                        onChangeDuration={(entryId, seconds) => updateDuration.mutate({ entryId, seconds })}
                    />
                ))}
            </div>

            {entries.length > 1 && (
                <>
                    <div className="h-px bg-border" />
                    <div className="flex items-center justify-between px-3 py-1.5 text-muted-foreground">
                        <span>Total</span>
                        <span className="tabular-nums text-orsane-primary">{formatDurationSeconds(totalSeconds)}</span>
                    </div>
                </>
            )}
        </div>
    );
}
